"use client";

import { memo, useState, useCallback, useEffect, useMemo, useRef } from "react";
import type { AnalysisSection, AnalysisPhrase, AnalysisRun } from "@/lib/music/analyzer-types";
import { SectionHeader } from "../../molecules/section-header";
import { MeasureGrid, type LoopRange } from "../../molecules/measure-grid";
import { buildPhraseGroups, isGroupLooped, type PhraseGroup } from "./phrase-groups";
import { resolveMeasureSelection } from "./measure-selection";
import { LoopActionBar } from "../../molecules/loop-action-bar";
import { recordSectionPick } from "@/lib/analytics/studio-session";

export type { LoopRange } from "../../molecules/measure-grid";

const sectionTypeColors: Record<string, string> = {
  verse: "#8b5cf6",
  chorus: "#f59e0b",
  bridge: "#06b6d4",
  outro: "#64748b",
  intro: "#a78bfa",
  section: "#6366f1",
  full: "#8b5cf6",
};

const labelColors = [
  "#8b5cf6", "#06b6d4", "#f59e0b", "#ec4899", "#22c55e",
  "#f97316", "#a78bfa", "#14b8a6", "#ef4444", "#6366f1",
  "#84cc16", "#e879f9", "#0ea5e9", "#fbbf24", "#10b981",
  "#f472b6", "#818cf8", "#2dd4bf", "#fb923c", "#c084fc",
];

function getSectionColor(section: AnalysisSection, index: number): string {
  return sectionTypeColors[section.type] ?? labelColors[index % labelColors.length];
}

/**
 * Capitalizes the analyzer's lowercase section type ("intro", "chorus")
 * for display. Kept as a tiny local helper, the analyzer payload is
 * the canonical lowercase form, the UI is the only place that needs
 * the cosmetic transform.
 */
function formatSectionType(type: string): string {
  if (type.length === 0) {
    return type;
  }

  return type.charAt(0).toUpperCase() + type.slice(1);
}

const NO_GROUPS: PhraseGroup[] = [];

interface SectionRowProps {
  section: AnalysisSection;
  phrases: AnalysisPhrase[];
  color: string;
  isExpanded: boolean;
  isCurrent: boolean;
  isLooped: boolean;
  /**
   * La mesure courante, ou `-1` quand la section est repliée.
   *
   * C'est ce qui fait tenir la mémoïsation : `currentMeasure` change à chaque
   * franchissement de barre, donc le passer à toutes les lignes les
   * invaliderait toutes. Une ligne repliée n'en a pas besoin — seul `isCurrent`
   * la concerne, et il ne bascule que sur deux lignes.
   */
  currentMeasure: number;
  loop: LoopRange | null;
  dragMin: number;
  dragMax: number;
  isDragSelecting: boolean;
  onToggleExpand: (id: string) => void;
  onToggleSectionLoop: (section: AnalysisSection) => void;
  onMeasurePointerDown: (m: number) => void;
  onMeasurePointerEnter: (m: number) => void;
  onSelectionEnd: () => void;
  onSelectionCancel: () => void;
  onToggleGroupLoop: (group: PhraseGroup) => void;
}

const SectionRow = memo(function SectionRow({
  section,
  phrases,
  color,
  isExpanded,
  isCurrent,
  isLooped,
  currentMeasure,
  loop,
  dragMin,
  dragMax,
  isDragSelecting,
  onToggleExpand,
  onToggleSectionLoop,
  onMeasurePointerDown,
  onMeasurePointerEnter,
  onSelectionEnd,
  onSelectionCancel,
  onToggleGroupLoop,
}: SectionRowProps) {
  // Une section repliée n'affiche aucun groupe : les calculer serait faire
  // six septièmes du travail pour rien, à chaque changement de mesure.
  const groups = useMemo(
    () => (isExpanded ? buildPhraseGroups(section, phrases) : NO_GROUPS),
    [isExpanded, section, phrases],
  );

  return (
    <div
      className="rounded-lg overflow-hidden"
      style={{
        backgroundColor:
          isExpanded || isCurrent ? "rgba(255,255,255,0.03)" : "transparent",
      }}
    >
      <SectionHeader
        label={formatSectionType(section.type)}
        startMeasure={section.startMeasure}
        endMeasure={section.endMeasure}
        color={color}
        isExpanded={isExpanded}
        isLooped={isLooped}
        onToggleExpand={() => onToggleExpand(section.id)}
        onToggleLoop={() => onToggleSectionLoop(section)}
      />

      {isExpanded && (
        <MeasureGrid
          groups={groups}
          currentMeasure={currentMeasure}
          loop={loop}
          dragMin={dragMin}
          dragMax={dragMax}
          isDragSelecting={isDragSelecting}
          onMeasurePointerDown={onMeasurePointerDown}
          onMeasurePointerEnter={onMeasurePointerEnter}
          onSelectionEnd={onSelectionEnd}
          onSelectionCancel={onSelectionCancel}
          onToggleGroupLoop={onToggleGroupLoop}
        />
      )}
    </div>
  );
});

/**
 * Une traversée du matériau, au-dessus des sections : une prise d'un
 * enregistrement, un morceau d'un enchaînement (`AnalysisRun`).
 *
 * Même ligne qu'une section — un nom, une étendue en mesures, un bouclage —
 * et, dépliée, ce qu'elle contient : ses sections quand elle en a, sa grille de
 * mesures sinon. Un récital enregistré n'a pas de sections : ses prises
 * s'ouvrent donc directement sur leurs mesures.
 */
interface RunRowProps {
  run: AnalysisRun;
  sections: AnalysisSection[];
  phrases: AnalysisPhrase[];
  color: string;
  isExpanded: boolean;
  isCurrent: boolean;
  isLooped: boolean;
  currentMeasure: number;
  loop: LoopRange | null;
  dragMin: number;
  dragMax: number;
  isDragSelecting: boolean;
  expandedSectionId: string | null;
  onToggleExpand: (id: string) => void;
  onToggleRunLoop: (run: AnalysisRun) => void;
  onToggleSectionExpand: (id: string) => void;
  onToggleSectionLoop: (section: AnalysisSection) => void;
  onMeasurePointerDown: (m: number) => void;
  onMeasurePointerEnter: (m: number) => void;
  onSelectionEnd: () => void;
  onSelectionCancel: () => void;
  onToggleGroupLoop: (group: PhraseGroup) => void;
}

const RunRow = memo(function RunRow({
  run,
  sections,
  phrases,
  color,
  isExpanded,
  isCurrent,
  isLooped,
  currentMeasure,
  loop,
  dragMin,
  dragMax,
  isDragSelecting,
  expandedSectionId,
  onToggleExpand,
  onToggleRunLoop,
  onToggleSectionExpand,
  onToggleSectionLoop,
  onMeasurePointerDown,
  onMeasurePointerEnter,
  onSelectionEnd,
  onSelectionCancel,
  onToggleGroupLoop,
}: RunRowProps) {
  const groups = useMemo(
    () => (isExpanded && sections.length === 0 ? buildPhraseGroups(run, phrases) : NO_GROUPS),
    [isExpanded, sections.length, run, phrases],
  );

  return (
    <div
      className="rounded-lg overflow-hidden"
      style={{
        backgroundColor: isExpanded || isCurrent ? "rgba(255,255,255,0.03)" : "transparent",
      }}
    >
      <SectionHeader
        label={run.label}
        startMeasure={run.startMeasure}
        endMeasure={run.endMeasure}
        color={color}
        isExpanded={isExpanded}
        isLooped={isLooped}
        onToggleExpand={() => onToggleExpand(run.id)}
        onToggleLoop={() => onToggleRunLoop(run)}
      />

      {isExpanded && sections.length > 0 && (
        <div className="pl-2 pb-1 space-y-0.5">
          {sections.map((section, index) => (
            <SectionRow
              key={section.id}
              section={section}
              phrases={phrases}
              color={getSectionColor(section, index)}
              isExpanded={expandedSectionId === section.id}
              isCurrent={currentMeasure >= section.startMeasure && currentMeasure <= section.endMeasure}
              isLooped={
                loop !== null &&
                loop.startMeasure === section.startMeasure &&
                loop.endMeasure === section.endMeasure
              }
              currentMeasure={expandedSectionId === section.id ? currentMeasure : -1}
              loop={loop}
              dragMin={expandedSectionId === section.id ? dragMin : -1}
              dragMax={expandedSectionId === section.id ? dragMax : -1}
              isDragSelecting={expandedSectionId === section.id && isDragSelecting}
              onToggleExpand={onToggleSectionExpand}
              onToggleSectionLoop={onToggleSectionLoop}
              onMeasurePointerDown={onMeasurePointerDown}
              onMeasurePointerEnter={onMeasurePointerEnter}
              onSelectionEnd={onSelectionEnd}
              onSelectionCancel={onSelectionCancel}
              onToggleGroupLoop={onToggleGroupLoop}
            />
          ))}
        </div>
      )}

      {isExpanded && sections.length === 0 && (
        <MeasureGrid
          groups={groups}
          currentMeasure={currentMeasure}
          loop={loop}
          dragMin={dragMin}
          dragMax={dragMax}
          isDragSelecting={isDragSelecting}
          onMeasurePointerDown={onMeasurePointerDown}
          onMeasurePointerEnter={onMeasurePointerEnter}
          onSelectionEnd={onSelectionEnd}
          onSelectionCancel={onSelectionCancel}
          onToggleGroupLoop={onToggleGroupLoop}
        />
      )}
    </div>
  );
});

interface SectionNavigatorProps {
  sections: AnalysisSection[];
  /**
   * Le découpage au-dessus des sections, quand il y en a un : les prises d'un
   * enregistrement. Vide pour un morceau ordinaire, et le sélecteur se comporte
   * alors exactement comme avant.
   */
  runs?: AnalysisRun[];
  phrases?: AnalysisPhrase[];
  currentMeasure?: number;
  loop: LoopRange | null;
  /**
   * Change la plage sélectionnée. `activate` force l'état du bouclage —
   * omis, il est conservé : sélectionner n'est pas boucler.
   */
  onLoopChange: (loop: LoopRange | null, activate?: boolean) => void;
  onSeekToMeasure?: (measure: number) => void;
  isLooping?: boolean;
  onToggleLooping?: () => void;
}

export function SectionNavigator({
  sections,
  runs = [],
  phrases = [],
  currentMeasure = 1,
  loop,
  onLoopChange,
  onSeekToMeasure,
  isLooping = false,
  onToggleLooping,
}: SectionNavigatorProps) {
  const currentSectionId = useMemo(() => {
    const current = sections.find(
      (s) => currentMeasure >= s.startMeasure && currentMeasure <= s.endMeasure,
    );

    return current?.id ?? null;
  }, [sections, currentMeasure]);

  /** Une seule section ouverte à la fois. */
  const [expandedId, setExpandedId] = useState<string | null>(
    () => currentSectionId,
  );

  /**
   * Dernière section ouverte par la lecture. On réagit au *changement* de
   * section, pas à sa valeur : tant que la tête de lecture reste dans la même
   * section, l'utilisateur peut en ouvrir une autre à la main sans que le
   * panneau la referme sous ses doigts.
   */
  const followedSectionRef = useRef<string | null>(currentSectionId);

  useEffect(() => {
    if (currentSectionId === null) {
      return;
    }

    if (currentSectionId === followedSectionRef.current) {
      return;
    }

    followedSectionRef.current = currentSectionId;
    setExpandedId(currentSectionId);
  }, [currentSectionId]);

  const currentRunId = useMemo(() => {
    const current = runs.find((r) => currentMeasure >= r.startMeasure && currentMeasure <= r.endMeasure);

    return current?.id ?? null;
  }, [runs, currentMeasure]);

  /**
   * La traversée dépliée. Elle suit la tête de lecture comme une section, et
   * à l'ouverture c'est la **dernière** qui compte : on s'arrête d'enregistrer
   * quand la prise est bonne, donc la bonne est presque toujours la dernière.
   */
  const [expandedRunId, setExpandedRunId] = useState<string | null>(
    () => currentRunId ?? runs[runs.length - 1]?.id ?? null,
  );

  // Pas de suivi automatique à ce niveau-là, contrairement aux sections : une
  // traversée dure des minutes, et la déplier sous les doigts de quelqu'un qui
  // compare deux prises lui reprendrait ce qu'il vient d'ouvrir. La ligne
  // courante s'éclaire, c'est tout.

  const toggleRun = useCallback((id: string) => {
    setExpandedRunId((prev) => (prev === id ? null : id));
  }, []);

  const handleToggleRunLoop = useCallback(
    (run: AnalysisRun) => {
      const alreadyLooped =
        loop !== null && loop.startMeasure === run.startMeasure && loop.endMeasure === run.endMeasure;

      if (alreadyLooped) {
        onLoopChange(null);

        return;
      }

      recordSectionPick();
      onLoopChange({ startMeasure: run.startMeasure, endMeasure: run.endMeasure }, true);
    },
    [loop, onLoopChange],
  );

  const [dragStart, setDragStart] = useState<number | null>(null);
  const [dragEnd, setDragEnd] = useState<number | null>(null);
  const didDragRef = useRef(false);

  const toggleSection = useCallback((id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  const handleToggleSectionLoop = useCallback(
    (section: AnalysisSection) => {
      const alreadyLooped =
        loop !== null &&
        loop.startMeasure === section.startMeasure &&
        loop.endMeasure === section.endMeasure;

      if (alreadyLooped) {
        onLoopChange(null);

        return;
      }

      // Désigner un passage dans Structure. Compté ici plutôt que dans la
      // page : tous les gestes qui désignent — la section entière, la phrase,
      // la mesure, le cerné de plusieurs — passent par ce composant, alors
      // que `onLoopChange` sert aussi au bouton « Boucler », qui repasse la
      // même plage sans que personne ne l'ait choisie une seconde fois.
      // Effacer n'est pas choisir, d'où la sortie ci-dessus.
      recordSectionPick();
      onLoopChange(
        { startMeasure: section.startMeasure, endMeasure: section.endMeasure },
        true,
      );
    },
    [loop, onLoopChange],
  );

  const handleMeasurePointerDown = useCallback((measure: number) => {
    setDragStart(measure);
    setDragEnd(measure);
    didDragRef.current = false;
  }, []);

  const handleMeasurePointerEnter = useCallback(
    (measure: number) => {
      if (dragStart !== null) {
        setDragEnd(measure);

        if (measure !== dragStart) {
          didDragRef.current = true;
        }
      }
    },
    [dragStart],
  );

  /**
   * Remet le geste à zéro sans rien valider.
   *
   * C'est aussi la fin de course de `pointercancel`, qui arrive pour de bon
   * sur mobile (appel entrant, geste système) : un glissé que l'OS interrompt
   * ne doit pas laisser une plage à moitié tracée à l'écran.
   */
  const resetSelection = useCallback(() => {
    setDragStart(null);
    setDragEnd(null);
    didDragRef.current = false;
  }, []);

  const handleSelectionEnd = useCallback(() => {
    const selection = resolveMeasureSelection(
      dragStart,
      dragEnd,
      didDragRef.current,
      loop,
    );

    switch (selection.kind) {
      case "clear":
        onLoopChange(null);
        break;

      case "range":
        recordSectionPick();
        onLoopChange(selection.loop);
        break;

      case "tap":
        // Un tap sélectionne la mesure — une plage d'une seule mesure, donc
        // bouclable — et s'y rend. Avant, il effaçait la sélection, ce qui
        // rendait toute boucle d'une mesure impossible à produire.
        //
        // Le déplacement qui suit est mené par l'application, donc muet pour
        // `onViewScrub` : ce geste est un choix de mesure, pas un `seek`, et
        // le compter deux fois le ferait exister dans les deux colonnes.
        recordSectionPick();
        onLoopChange(selection.loop);
        onSeekToMeasure?.(selection.loop.startMeasure);
        break;

      case "none":
        break;
    }

    resetSelection();
  }, [dragStart, dragEnd, loop, onLoopChange, onSeekToMeasure, resetSelection]);

  /**
   * La poignée sélectionne la phrase et s'y rend — elle ne l'arme pas.
   *
   * `activate` est omis à dessein : désigner une phrase est un repérage, au
   * même titre qu'un clic ou un cerné sur les mesures. C'est le bouton
   * « Boucler » qui décide qu'on tourne en rond, et lui seul.
   */
  const handleToggleGroupLoop = useCallback(
    (group: PhraseGroup) => {
      if (isGroupLooped(group, loop)) {
        onLoopChange(null);

        return;
      }

      recordSectionPick();
      onLoopChange({
        startMeasure: group.startMeasure,
        endMeasure: group.endMeasure,
      });
      onSeekToMeasure?.(group.startMeasure);
    },
    [loop, onLoopChange, onSeekToMeasure],
  );

  const isDragSelecting = dragStart !== null && dragEnd !== null && didDragRef.current;
  const dragMin = isDragSelecting ? Math.min(dragStart, dragEnd) : -1;
  const dragMax = isDragSelecting ? Math.max(dragStart, dragEnd) : -1;

  if (sections.length === 0 && runs.length === 0) {
    return (
      <div className="flex flex-col h-full select-none">
        <div className="px-3 pt-2.5 pb-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-accent-bright">
            Structure
          </span>
        </div>
        <div className="flex-1 flex items-center justify-center px-4">
          <p className="text-[11px] text-text-muted text-center">
            Importe un fichier MIDI pour voir la structure du morceau
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full select-none">
      {/* Header */}
      <div className="px-3 pt-2.5 pb-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-accent-bright">
          Structure
        </span>
      </div>

      {/* Section list */}
      <div className="flex-1 overflow-y-auto px-2 pb-1 space-y-0.5">
        {runs.map((run, rIdx) => (
          <RunRow
            key={run.id}
            run={run}
            sections={sections.filter(
              (s) => s.startMeasure >= run.startMeasure && s.endMeasure <= run.endMeasure,
            )}
            phrases={phrases}
            color={labelColors[rIdx % labelColors.length]}
            isExpanded={expandedRunId === run.id}
            isCurrent={currentMeasure >= run.startMeasure && currentMeasure <= run.endMeasure}
            isLooped={
              loop !== null &&
              loop.startMeasure === run.startMeasure &&
              loop.endMeasure === run.endMeasure
            }
            currentMeasure={expandedRunId === run.id ? currentMeasure : -1}
            loop={loop}
            dragMin={expandedRunId === run.id ? dragMin : -1}
            dragMax={expandedRunId === run.id ? dragMax : -1}
            isDragSelecting={expandedRunId === run.id && isDragSelecting}
            expandedSectionId={expandedId}
            onToggleExpand={toggleRun}
            onToggleRunLoop={handleToggleRunLoop}
            onToggleSectionExpand={toggleSection}
            onToggleSectionLoop={handleToggleSectionLoop}
            onMeasurePointerDown={handleMeasurePointerDown}
            onMeasurePointerEnter={handleMeasurePointerEnter}
            onSelectionEnd={handleSelectionEnd}
            onSelectionCancel={resetSelection}
            onToggleGroupLoop={handleToggleGroupLoop}
          />
        ))}

        {/* Les sections qu'aucune traversée ne contient — et toutes, quand il
            n'y en a pas : c'est le cas de tout morceau ordinaire. */}
        {sections
          .filter((s) => !runs.some((r) => s.startMeasure >= r.startMeasure && s.endMeasure <= r.endMeasure))
          .map((section, sIdx) => {
          const isExpanded = expandedId === section.id;

          return (
            <SectionRow
              key={section.id}
              section={section}
              phrases={phrases}
              color={getSectionColor(section, sIdx)}
              isExpanded={isExpanded}
              isCurrent={
                currentMeasure >= section.startMeasure &&
                currentMeasure <= section.endMeasure
              }
              isLooped={
                loop !== null &&
                loop.startMeasure === section.startMeasure &&
                loop.endMeasure === section.endMeasure
              }
              currentMeasure={isExpanded ? currentMeasure : -1}
              loop={loop}
              dragMin={isExpanded ? dragMin : -1}
              dragMax={isExpanded ? dragMax : -1}
              isDragSelecting={isExpanded && isDragSelecting}
              onToggleExpand={toggleSection}
              onToggleSectionLoop={handleToggleSectionLoop}
              onMeasurePointerDown={handleMeasurePointerDown}
              onMeasurePointerEnter={handleMeasurePointerEnter}
              onSelectionEnd={handleSelectionEnd}
              onSelectionCancel={resetSelection}
              onToggleGroupLoop={handleToggleGroupLoop}
            />
          );
        })}
      </div>

      {/* Bottom action bar */}
      <LoopActionBar
        loop={loop}
        onClear={() => onLoopChange(null)}
        isLooping={isLooping}
        onToggleLooping={onToggleLooping}
      />
    </div>
  );
}
